import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Play,
  Pause,
  SkipForward,
  CheckCircle,
  ArrowLeft,
  Clock,
  Heart,
  Users,
  Timer,
  ChevronRight,
  RotateCcw,
  Volume2,
  VolumeX,
  Flame
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

// Voice function
const speakText = (text, rate = 1.0) => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = 1.1;
    window.speechSynthesis.speak(utterance);
  }
};

export default function PartnerWorkout() {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const [restTime, setRestTime] = useState(30);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [workoutComplete, setWorkoutComplete] = useState(false);

  useEffect(() => {
    fetchWorkout();
  }, [workoutId]);

  useEffect(() => {
    let interval;
    if (isPlaying && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimerComplete();
            return 0;
          }
          // Countdown voice
          if (prev <= 3 && voiceEnabled) {
            speakText(String(prev), 1.2);
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, timeLeft]);

  const fetchWorkout = async () => {
    try {
      const response = await axios.get(`${API}/workouts/partner/${workoutId}`);
      setWorkout(response.data);
      if (response.data.exercises?.length > 0) {
        const firstEx = response.data.exercises[0];
        setTimeLeft(firstEx.duration_seconds || 45);
      }
      if (voiceEnabled) {
        setTimeout(() => speakText(`Partner workout: ${response.data.title}. Get ready with your partner!`), 500);
      }
    } catch (error) {
      toast.error("Failed to load partner workout");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleTimerComplete = () => {
    if (isResting) {
      setIsResting(false);
      const exercise = workout.exercises[currentExerciseIndex];
      setTimeLeft(exercise.duration_seconds || 45);
      if (voiceEnabled) {
        speakText(`Set ${currentSet}. ${exercise.name}. ${exercise.partner_role}. Let's go!`);
      }
    } else {
      if (currentSet < (workout.exercises[currentExerciseIndex].sets || 3)) {
        setCurrentSet(prev => prev + 1);
        setIsResting(true);
        setTimeLeft(restTime);
        if (voiceEnabled) speakText("Rest time. Great job team!");
      } else {
        handleNextExercise();
      }
    }
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < workout.exercises.length - 1) {
      const nextIndex = currentExerciseIndex + 1;
      setCurrentExerciseIndex(nextIndex);
      setCurrentSet(1);
      setIsResting(false);
      const nextEx = workout.exercises[nextIndex];
      setTimeLeft(nextEx.duration_seconds || 45);
      if (voiceEnabled) {
        speakText(`Next up: ${nextEx.name}. ${nextEx.partner_role}`);
      }
    } else {
      setWorkoutComplete(true);
      setIsPlaying(false);
      if (voiceEnabled) {
        speakText("Amazing! You both crushed this workout together! That's teamwork!");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Heart className="h-12 w-12 text-pink-500 mx-auto mb-4 animate-pulse" />
          <p className="text-primary font-bold">Loading Partner Workout...</p>
        </div>
      </div>
    );
  }

  if (workoutComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="mb-6"
          >
            <Heart className="h-24 w-24 text-pink-500 mx-auto fill-pink-500" />
          </motion.div>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-4">
            COUPLES GOAL!
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            You and your partner crushed it together! 💪❤️
          </p>
          <Link to="/dashboard">
            <Button className="bg-pink-500 hover:bg-pink-600 uppercase font-bold tracking-widest">
              Back to Dashboard
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const currentExercise = workout?.exercises[currentExerciseIndex];
  const totalSets = currentExercise?.sets || 3;
  const progress = ((currentExerciseIndex / workout.exercises.length) * 100);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-pink-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/partner-workouts" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Exit
            </Link>
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-pink-500 fill-pink-500" />
              <span className="font-bold tracking-tight text-pink-500">PARTNER MODE</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className={voiceEnabled ? 'text-pink-500' : 'text-muted-foreground'}
            >
              {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-20 pb-32 px-4 max-w-2xl mx-auto">
        {/* Workout Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-black uppercase tracking-tighter text-pink-500">{workout.title}</h1>
          <p className="text-sm text-muted-foreground">{workout.description}</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Exercise {currentExerciseIndex + 1} of {workout.exercises.length}</span>
            <span className="text-pink-500 font-bold">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Current Exercise */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentExerciseIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
          >
            <Card className={`bg-card border-2 ${isResting ? 'border-green-500' : 'border-pink-500'}`}>
              <CardContent className="p-6">
                {isResting ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">😮‍💨</div>
                    <h2 className="text-2xl font-black uppercase text-green-500 mb-2">REST</h2>
                    <p className="text-muted-foreground">Catch your breath together</p>
                  </div>
                ) : (
                  <>
                    {/* Exercise Name */}
                    <h2 className="text-2xl font-black uppercase tracking-tighter text-center mb-2">
                      {currentExercise.name}
                    </h2>
                    <p className="text-center text-muted-foreground mb-4">
                      {currentExercise.description}
                    </p>

                    {/* Partner Role Badge */}
                    <div className="flex justify-center mb-6">
                      <div className="px-4 py-2 bg-pink-500/20 rounded-full border border-pink-500/50">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-pink-500" />
                          <span className="text-sm font-bold text-pink-500">{currentExercise.partner_role}</span>
                        </div>
                      </div>
                    </div>

                    {/* Tips */}
                    {currentExercise.tips && (
                      <div className="bg-secondary/30 rounded-lg p-3 mb-4">
                        <p className="text-sm text-center">
                          💡 <span className="text-muted-foreground">{currentExercise.tips}</span>
                        </p>
                      </div>
                    )}

                    {/* Reps/Duration */}
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-secondary/50 rounded-lg p-3">
                        <div className="text-2xl font-black text-pink-500">
                          {currentExercise.reps || `${currentExercise.duration_seconds}s`}
                        </div>
                        <div className="text-xs text-muted-foreground uppercase">
                          {currentExercise.reps ? 'Reps' : 'Duration'}
                        </div>
                      </div>
                      <div className="bg-secondary/50 rounded-lg p-3">
                        <div className="text-2xl font-black">
                          {currentSet}/{totalSets}
                        </div>
                        <div className="text-xs text-muted-foreground uppercase">Set</div>
                      </div>
                    </div>
                  </>
                )}

                {/* Timer */}
                <div className="text-center my-8">
                  <div className={`text-7xl font-mono font-black ${isResting ? 'text-green-500' : 'text-pink-500'}`}>
                    {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
                  </div>
                </div>

                {/* Controls */}
                <div className="flex justify-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setTimeLeft(currentExercise.duration_seconds || 45);
                      setIsPlaying(false);
                    }}
                    className="h-14 w-14 rounded-full"
                  >
                    <RotateCcw className="h-6 w-6" />
                  </Button>
                  
                  <Button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className={`h-20 w-20 rounded-full ${
                      isPlaying 
                        ? 'bg-orange-500 hover:bg-orange-600' 
                        : 'bg-pink-500 hover:bg-pink-600'
                    }`}
                  >
                    {isPlaying ? (
                      <Pause className="h-10 w-10" />
                    ) : (
                      <Play className="h-10 w-10 ml-1" />
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleNextExercise}
                    className="h-14 w-14 rounded-full"
                  >
                    <SkipForward className="h-6 w-6" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Muscle Groups */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Target Muscles</p>
          <div className="flex flex-wrap justify-center gap-2">
            {currentExercise?.muscle_groups?.map((muscle, i) => (
              <span key={i} className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-xs capitalize">
                {muscle.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
